import { NgClass } from "@angular/common";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { AuthService } from "@app/core/auth/auth.service";
import {
  FuseNavigationItem,
  FuseNavigationService,
} from "@fuse/components/navigation";
import { FuseConfigService } from "@fuse/services/config";
import { FuseThemeService } from "@fuse/services/theme/theme.service";
import { McResidusLayoutComponent } from "@layout/layouts/mcresidus-layout/mcresidus-layout.component";
import { NgSelectConfig } from "@ng-select/ng-select";
import { Subject, Subscription, takeUntil } from "rxjs";
import { NavigationEnd, Router } from "@angular/router";
import { LayoutService } from "@utils/layout.service";
import { defaultNavigation } from "./core/navigation/navigationMap";
import { RuntimeConfigService } from "./runtime-config.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  imports: [McResidusLayoutComponent, NgClass],
  providers: [],
})
export class AppComponent implements OnInit, OnDestroy {
  mainColor = "theme_original";
  private themeSubscription: Subscription;

  private _bcnConfigService = inject(FuseConfigService);
  private _bcnNavigationService = inject(FuseNavigationService);
  private config = inject(NgSelectConfig);
  themeService = inject(FuseThemeService);
  private dialog = inject(MatDialog);
  private authService = inject(AuthService);
  private runtime = inject(RuntimeConfigService);
  private layoutService = inject(LayoutService);
  private _router = inject(Router);

  translate = {
    categoria: "categorias",
    fraccions: "fraccions",
    "punt-recollida": "punts de recollida",
    residus: "residus",
    "informacio-secundaria": "informació secundaria",
    "tipus-servei": "tipus de servei",
    especial: "especial",
    sinonims: "sinònims",
    horaris: "horaris CityOS",
  };

  navigation: FuseNavigationItem[];
  bcnConfig: any;

  // Private
  private _unsubscribeAll: Subject<void>;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  /**
   * Constructor
   *
   */
  constructor(protected themeService2: FuseThemeService) {
    // Get default navigation
    this.navigation = defaultNavigation;

    // Register the navigation to the service
    this._bcnNavigationService.register("main", this.navigation);

    // Set the main navigation as our current navigation
    this._bcnNavigationService.setCurrentNavigation("main");

    // Set the private defaults
    this._unsubscribeAll = new Subject();

    // Configure default ng-select
    this.config.notFoundText = "Custom not found";
    this.config.placeholder = "Seleccioneu l'element";
    this.config.notFoundText = "No hi ha elements";
    this.config.addTagText = "Afegir element";
    this.config.typeToSearchText = "Cerca...";
    this.config.loadingText = "Carregant...";
    this.config.clearAllText = "Esborrar";

    this.themeSubscription = this.themeService.currentTheme$.subscribe(
      (theme) => {
        this.mainColor = theme;
      }
    );
    this._router.events.subscribe((value) => {
      if (value instanceof NavigationEnd) {
        this.parseUrlBreadcrumbs(value.url);
      }
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    if (this.authService.getAccessToken()) {
      // Subscribe to config changes
      this._bcnConfigService.config?.pipe(takeUntil(this._unsubscribeAll)).subscribe((config: any) => {
        this.bcnConfig = config;
      });
      this.authService.getUser().subscribe();
      this.authService.getRefreshToken();
    } else {
      if (localStorage.getItem("refreshToken") && localStorage.getItem("refreshToken") !== "undefined") {
        this.authService.getRefreshToken();
      } else {
        this.authService.redirectIDP();
      }
      // }
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this.themeSubscription.unsubscribe(); // Evitar memory leaks
  }

  parseUrlBreadcrumbs(url: string) {
    const regexVowel = /[aeiou]/g;
    const regexNumber = /[0-9]/g;
    const regexSymbol = /[#?]/g;
    const value = url.split(regexSymbol)[0].split("/").slice(1);

    if (value.length > 0) {
      const firstSegment = value[0].split(regexSymbol)[0].replaceAll("_", " ");
      let title: string;
      title = this.translate[firstSegment];

      if (value.length == 1) {
        if (
          (value[0][0] && value[0][0].match(regexVowel)) ||
          title?.[0]?.toLowerCase().match(regexVowel)
        ) {
          this.layoutService.setShowTitle("Llista d'" + title);
        } else {
          this.layoutService.setShowTitle("Llistat de " + title);
        }
      } else {
        if (value.length == 2) {
          if (value[1].match(regexNumber)) {
            if (value[0][0].match(regexVowel)) {
              this.layoutService.setShowTitle("Detall d'" + title);
            } else {
              this.layoutService.setShowTitle("Detall de " + title);
            }
          } else {
            if (value[0][0].match(regexVowel)) {
              this.layoutService.setShowTitle("Creació d'" + title);
            } else {
              this.layoutService.setShowTitle("Creació de " + title);
            }
          }
        } else {
          if (value[1].match(regexNumber)) {
            if (
              value[0][0].match(regexVowel) ||
              title?.[0]?.toLowerCase().match(regexVowel)
            ) {
              this.layoutService.setShowTitle("Edició d'" + title);
            } else {
              this.layoutService.setShowTitle("Edició de " + title);
            }
          }
        }
      }

      this.layoutService.cleanBreadcrumbs();
      this.layoutService.pushBreadcrumb({
        label: "inici".toUpperCase(),
        url: "/map",
      });
      for (const url of value) {
        if (!url.match(regexNumber)) {
          this.layoutService.pushBreadcrumb({
            label: this.translate[url.replaceAll("_", " ")]?.toUpperCase(),
            url: undefined,
          });
        }
      }
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
}
